import uiCore = require("./uicore");
import coreconstants = require("./core.constants");
import breeze = require("breeze-client");
import angularServiceHelper = require("./angularservicehelper");
import shellControler = require("../layout/shell.controller");
import errorpage = require("./errorpage");

export interface IEntityWithId {
    Id: number;
};

export interface IArrayCompareResult<T_Entity> {
    addedEntities: T_Entity[];
    removedEntities: T_Entity[];
}

export interface IArrayChangeHandler<T_Entity> {
    applyAdd: (entityManager: breeze.EntityManager, addedEntities: T_Entity[]) => void;
    applyRemove: (entityManager: breeze.EntityManager, removedEntities: T_Entity[]) => void;
}

function datectArrayChanges<T_Entity extends IEntityWithId>(originalItems: T_Entity[], items: T_Entity[]): IArrayCompareResult<T_Entity> {
    return {
        addedEntities: items.filter(it => originalItems.every(itt => itt.Id != it.Id)),
        removedEntities: originalItems.filter(it => items.every(itt => itt.Id != it.Id))
    };
}

function applyDetectedArrayChanges<T_Entity>(entityManager: breeze.EntityManager, changes: IArrayCompareResult<T_Entity>, changeHandler: IArrayChangeHandler<T_Entity>) {
    changeHandler.applyAdd(entityManager, changes.addedEntities);
    changeHandler.applyRemove(entityManager, changes.removedEntities);
}

function applyArrayChanges<T_Entity extends IEntityWithId>(entityManager: breeze.EntityManager, originalItems: T_Entity[], currentItems: T_Entity[], changeHandler: IArrayChangeHandler<T_Entity>) {
    var detectedChanges = <IArrayCompareResult<T_Entity>> datectArrayChanges(originalItems, currentItems);
    applyDetectedArrayChanges<T_Entity>(entityManager, detectedChanges, changeHandler);
}

function arrayFind<T_Item, T_Items>(item: T_Item, items: T_Items[], compareFunc: (item2: T_Items, item1: T_Item) => boolean): T_Items {
    for (var i = 0; i < items.length; i++) {
        if (compareFunc(items[i], item)) {
            return items[i];
        }
    };
    return null;
}

function saveSequentialListOfChangesIndex(deferred: ng.IDeferred<{}>, taskIndex: number, listOfApplyChanges: (() => void | ng.IPromise<any>)[], entityManager: breeze.EntityManager) {
    if (taskIndex >= listOfApplyChanges.length) {
        deferred.resolve("All changes saved");
    }
    else {
        // Perform tasks at index
        var promise = listOfApplyChanges[taskIndex]();

        var onPromise = () => {
            entityManager.getChanges();
            entityManager.saveChanges()
                .then(_breezeResult => {
                saveSequentialListOfChangesIndex(deferred, taskIndex + 1, listOfApplyChanges, entityManager);
            }).catch(reason => {
                deferred.reject(reason);
            })
        };

        if (!promise) onPromise();
        else {
            (<ng.IPromise<any>>promise)
                .then(_res => onPromise())
                .catch(reason => {
                    deferred.reject(reason);
            })
        }
    }
}

function saveSequentialListOfChanges(listOfApplyChanges: (() => void)[], entityManager: breeze.EntityManager): ng.IPromise<any> {
    var deferred = angularServiceHelper.Services.$q.defer();
    saveSequentialListOfChangesIndex(deferred, 0, listOfApplyChanges, entityManager);
    return deferred.promise;
}

export function saveSequentialListOfChangesAndNavigate(listOfApplyChanges: (() => void)[], entityManager: breeze.EntityManager, onFinished: () => void, $state: ng.ui.IStateService) {
    
    shellControler.enterAppModal();
    saveSequentialListOfChanges(listOfApplyChanges, entityManager)
        .then(_res => {
        shellControler.leaveAppModal();
        onFinished()
    })
        .catch(reason => {
        shellControler.leaveAppModal();
        errorpage.ErrorLog.registerAndGoToErrorPage(reason, $state);
    });
}

export function confirmedSaveSequentialListOfChangesAndNavigate(listOfApplyChanges: (() => void)[], title: string, content: string, entityManager: breeze.EntityManager, $mdDialog: ng.material.IDialogService, onFinished: () => void, $state: ng.ui.IStateService) {
    
    var confirm = $mdDialog
        .confirm()
        .title(title)
        .textContent(content)
        .ok("Yes")
        .cancel("Cancel");

    $mdDialog.show(confirm).then(() => {
        saveSequentialListOfChangesAndNavigate(listOfApplyChanges, entityManager, onFinished, $state);
    });
}

export interface IArrayItemHandler<T_Source, T_Entity> {
    sourceCreateEntity: (entity: T_Entity) => T_Source;
    convertToEditedEntity: (entity: T_Source) => T_Entity;
}

export interface ISourceItemCompare<T_Source, T_Entity> {
    (sourceEntity: T_Source, editedEntity: T_Entity) : boolean;
}

export interface IArrayOfEntitiesEditHelperInitializer<T_Source, T_Entity extends IEntityWithId> {
    sourceEntityTypeName: string;
    source: T_Source[];
    entityManager: breeze.EntityManager;
    sourceCreateEntity: (entity: T_Entity) => T_Source;
    convertToEditedEntity: (entity: T_Source) => T_Entity;
    sourceItemCompare: ISourceItemCompare<T_Source, T_Entity>;
}

export class ArrayOfEntitiesEditHelper<T_Source, T_Entity extends IEntityWithId> {
    private _changeHandler: IArrayChangeHandler<T_Entity>;
    private _originalItems: T_Entity[];
    private _editedItems: T_Entity[];
    private _entityManager: breeze.EntityManager;

    public initialize(settings: IArrayOfEntitiesEditHelperInitializer<T_Source, T_Entity>): T_Entity[]{
        this._entityManager = settings.entityManager;
        this._changeHandler = <IArrayChangeHandler<T_Entity>> {
            applyAdd: (_manager, added) => {
                for (var i = 0; i < added.length; i++) {
                    var newEntity = settings.sourceCreateEntity(added[i]);
                    settings.entityManager.createEntity(settings.sourceEntityTypeName, newEntity);
                }
            },
            applyRemove: (_manager, removed) => {
                for (var i = 0; i < removed.length; i++) {
                    var removedRef = arrayFind<T_Entity, T_Source>(removed[i], settings.source, settings.sourceItemCompare);
                    if ((<any>removedRef).entityAspect) {
                        (<any>removedRef).entityAspect.setDeleted();
                    }
                }
            }
        };

        this._originalItems = settings.source.map(src => settings.convertToEditedEntity(src));
        this._editedItems = this._originalItems.slice();
        return this._editedItems;
    }

    public hasChanges(): boolean {
        if (this._editedItems.length != this._originalItems.length) {
            return true;
        }

        for (var i = 0; i < this._editedItems.length; i++) {
            if (this._originalItems.indexOf(this._editedItems[i]) < 0) {
                return true;
            }
        }
        return false;
    }

    public applyChanges() {
        if (this.hasChanges()) {
            applyArrayChanges(this._entityManager, this._originalItems, this._editedItems, this._changeHandler);
        }
    }
}

export class FileContentHelper {
    public static saveFileContent(file: File, serviceUrl: string): ng.IHttpPromise<any> {
        // Base service url: "PersonService/" + personId + "/PersonImage"
        var url = FileContentHelper.FormatFullUrl(serviceUrl + "?fileName=" + encodeURIComponent(file.name));
        return angularServiceHelper.Services.$http.put(url, file);
    }

    public static deleteFileContent(serviceUrl: string): ng.IHttpPromise<any> {
        // Base service url: "PersonService/" + personId + "/PersonImage"
        var url = FileContentHelper.FormatFullUrl(serviceUrl);
        return angularServiceHelper.Services.$http.delete(url);
    }

    public static FormatPersonImageServiceUrl(personId: number) {
        return "PersonService/" + personId + "/PersonImage";
    }

    public static FormatPersonCompetenceAttachmentServiceUrl(personCompetenceId: number) {
        return "PersonService/PersonCompetenceAttachment/" + personCompetenceId;
    }

    public static FormatFullUrl(serviceUrl: string): string {
        return coreconstants.ServiceConfig.webApiUrl + serviceUrl;;
    }
    
    public hasAttachment: boolean;
    private _$mdDialog: ng.material.IDialogService;
    private _$scope: ng.IScope;

    public constructor($mdDialog: ng.material.IDialogService, $scope: ng.IScope, setLocalSource: boolean, hasAttachmentInitially: boolean, private maxNrMBytes: number) {
        this._$mdDialog = $mdDialog;
        this._$scope = $scope;
        this._setLocalSource = setLocalSource;
        this.hasAttachment = hasAttachmentInitially;
    }

    private _deleteImage: boolean;
    private _file: File;
    private _setLocalSource: boolean;

    showAlert(title: string, content: string): ng.IPromise<any> {
        var confirm = this._$mdDialog.alert()
            .title(title)
            .textContent(content)
            .ok('Ok');

        return this._$mdDialog.show(confirm)
    }
    
    public selectFile = (file: File) => {
        if (file.size > this.maxNrMBytes * 1024 * 1024) { // 2 mb
            this.showAlert("File to big", "Maximum allowed file size is " + this.maxNrMBytes + "mb.");
            return;
        };

        this._deleteImage = false;
        this.hasAttachment = true;
        this._file = file;

        uiCore.safeScopeApply(this._$scope);

        if (this._setLocalSource) {
            var reader = new FileReader();
            reader.onload = (_) => {
                if (!this._deleteImage) {
                    //this._localSource = (<any>ev.target).result;
                }
                uiCore.safeScopeApply(this._$scope);
            };

            reader.readAsDataURL(file);
        }
    }

    public deleteFile = () => {
        this._deleteImage = true;
        this._file = null;
        this.hasAttachment = false;
        if (this._setLocalSource) {
            //this._localSource = "#";
        }
        uiCore.safeScopeApply(this._$scope);
    }

    public hasChanges = (): boolean  => {
        return (this._file != null) || this._deleteImage;
    }

    public applyChanges(serviceUrl: string): ng.IPromise<any> {
        if (this._deleteImage) {
            return FileContentHelper.deleteFileContent(serviceUrl);
        } else if (this._file) {
            return FileContentHelper.saveFileContent(this._file, serviceUrl);
        } else {
            return null;
        }

    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/core/entityhelper.ts